/**
 * YouTube channel (read-only; flag social.youtube). Panel on /integrations: connected channels with health, daily stats,
 * reconnect and disconnect. Connect: POST /v1/social/youtube/oauth/start -> Google consent (youtube.readonly) -> the
 * worker's /bff/oauth/connectors/callback -> /integrations/connect, where ConnectCallbackPage hands `yts_` states to
 * YouTubeConnectCallback below (POST /v1/social/youtube/oauth/callback, channel picker, /oauth/select).
 * While the flag is off the API answers 404 and the panel renders nothing.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { YouTubeAccountView, YouTubeChannelOption } from '@qm/contracts/social';
import { Badge, Button, ErrorState, Heading, Loading, Text } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { BffError, bff, bffJson } from '../../../lib/bff';
import { useFormat } from '../../../lib/format';

/** Error codes the screens explain; anything else gets the generic line (codes never carry secrets). */
const KNOWN = ['oauth_state_expired', 'missing_scopes', 'no_channels', 'channel_mismatch', 'channel_connected_elsewhere', 'provider_not_configured'] as const;
export function youtubeErrorKey(code: string | null | undefined): string {
  if (code === 'access_denied' || code === 'user_denied') return 'social.youtube.error.denied';
  return (KNOWN as readonly string[]).includes(code ?? '') ? `social.youtube.error.${code}` : 'social.youtube.error.generic';
}
const codeOf = (e: unknown) => (e instanceof BffError ? ((e.envelope?.error.details as { code?: string } | undefined)?.code ?? null) : null);
const safeUrl = (u: unknown) => { try { return typeof u === 'string' && new URL(u).protocol === 'https:' ? u : null; } catch { return null; } };

export function YouTubePanel() {
  const { t } = useApp();
  const f = useFormat();
  const [accounts, setAccounts] = useState<YouTubeAccountView[] | null>(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = () => bff<YouTubeAccountView[]>('/v1/social/youtube/accounts').then(setAccounts, (e: unknown) => { if (e instanceof BffError && e.status === 404) setHidden(true); else setError('social.youtube.error.generic'); });
  useEffect(() => { void load(); }, []);
  if (hidden) return null;

  const start = async (reconnectAccountId?: string) => {
    setBusy(true); setError(null);
    try {
      const r = await bffJson<{ authorizeUrl: string }>('/v1/social/youtube/oauth/start', 'POST', reconnectAccountId ? { reconnectAccountId } : {});
      const url = safeUrl(r.authorizeUrl);
      if (!url) throw new Error('bad authorize url');
      window.location.assign(url);
    } catch (e) { setError(youtubeErrorKey(codeOf(e))); setBusy(false); }
  };
  const disconnect = async (id: string) => {
    setBusy(true); setError(null);
    try { await bffJson(`/v1/social/accounts/${encodeURIComponent(id)}`, 'DELETE'); await load(); } catch (e) { setError(youtubeErrorKey(codeOf(e))); }
    setBusy(false);
  };

  return (
    <section className="cu-integrations__section" aria-labelledby="youtube-title" data-testid="youtube-panel">
      <div className="cu-integrations__section-head">
        <div>
          <Heading level={2} id="youtube-title">{t('social.youtube.title')}</Heading>
          <Text tone="muted">{t('social.youtube.intro')}</Text>
        </div>
        <Button onClick={() => void start()} disabled={busy}>{t('social.youtube.connect')}</Button>
      </div>
      <Text tone="muted">{t('social.youtube.readOnly')}</Text>
      {error ? <Text role="alert">{t(error)}</Text> : null}
      {accounts === null && !error ? <Loading label={t('social.youtube.loading')} /> : null}
      {accounts && !accounts.length ? <Text>{t('social.youtube.empty')}</Text> : null}
      {accounts?.length ? (
        <ul className="cu-integrations__provider-list">
          {accounts.map((a) => (
            <li className="cu-integrations__provider" key={a.id} data-testid={`youtube-${a.channelId}`}>
              <div className="cu-integrations__provider-name">
                <span><bdi>{a.displayName}</bdi></span>
                <Badge tone={a.action === 'reconnect' || a.health === 'provider_issue' ? 'error' : 'neutral'}>{t(`social.youtube.health.${a.health}`)}</Badge>
              </div>
              {a.stats ? (
                <Text tone="muted">
                  {t('social.youtube.stats.subscribers')}: {a.stats.subscribers === null ? t('social.youtube.stats.hidden') : f.number(a.stats.subscribers)}
                  {' · '}{t('social.youtube.stats.views')}: {f.number(a.stats.views)}
                  {' · '}{t('social.youtube.stats.videos')}: {f.number(a.stats.videos)}
                  {' · '}{t('social.youtube.stats.asOf', { date: f.date(a.stats.fetchedAt) })}
                </Text>
              ) : a.status === 'connected' ? <Text tone="muted">{t('social.youtube.stats.none')}</Text> : null}
              <div>
                {a.action === 'reconnect' ? <Button onClick={() => void start(a.id)} disabled={busy}>{t('social.youtube.reconnect')}</Button> : null}
                {a.status === 'connected' ? <Button onClick={() => void disconnect(a.id)} disabled={busy}>{t('social.youtube.disconnect')}</Button> : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

export interface YouTubeCallbackParams { state: string | null; code: string | null; error: string | null }

/** /integrations/connect for `yts_` states: finish the connection, pick channels when there are several. */
export function YouTubeConnectCallback({ params }: { params: YouTubeCallbackParams }) {
  const { t } = useApp();
  const [phase, setPhase] = useState<'working' | 'choose' | 'error'>('working');
  const [errorKey, setErrorKey] = useState('social.youtube.error.generic');
  const [pending, setPending] = useState<{ id: string; channels: YouTubeChannelOption[] } | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const started = useRef(false);
  const fail = (code: string | null | undefined) => { setErrorKey(youtubeErrorKey(code)); setPhase('error'); };
  const done = (reconnected: boolean) => window.location.assign(`/integrations?youtube=${reconnected ? 'reconnected' : 'connected'}`);

  useEffect(() => {
    if (typeof window === 'undefined' || started.current) return;
    started.current = true;
    // the code is single-use, but it must not stay in history or be re-posted on reload
    history.replaceState(null, '', window.location.pathname);
    if (params.error || !params.state || !params.code) { fail(params.error); return; }
    bffJson<{ pendingId: string | null; channels: YouTubeChannelOption[]; reconnected: boolean }>('/v1/social/youtube/oauth/callback', 'POST', { state: params.state, code: params.code }).then((r) => {
      if (!r.pendingId) { done(r.reconnected); return; }
      setPending({ id: r.pendingId, channels: r.channels });
      setPhase('choose');
    }, (e: unknown) => fail(codeOf(e)));
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!pending || !chosen.size) return;
    try { await bffJson('/v1/social/youtube/oauth/select', 'POST', { pendingId: pending.id, channelIds: [...chosen] }); done(false); } catch (err) { fail(codeOf(err)); }
  };
  const toggle = (id: string, on: boolean) => setChosen((s) => { const n = new Set(s); if (on) n.add(id); else n.delete(id); return n; });

  return (
    <section className="qm-page" aria-labelledby="page-title">
      <div className="qm-page__head"><Heading level={1} id="page-title">{t('social.youtube.title')}</Heading></div>
      {phase === 'working' ? <Loading label={t('social.youtube.callback.working')} /> : null}
      {phase === 'error' ? (
        <div role="alert">
          <ErrorState message={t(errorKey)} />
          <Button onClick={() => window.location.assign('/integrations')}>{t('social.youtube.callback.back')}</Button>
        </div>
      ) : null}
      {phase === 'choose' && pending ? (
        <form onSubmit={(e) => void submit(e)}>
          <fieldset>
            <legend>{t('social.youtube.pick.title')}</legend>
            {pending.channels.map((c, i) => (
              <div key={c.channelId}>
                <input type="checkbox" id={`yt-${i}`} checked={chosen.has(c.channelId)} onChange={(ev) => toggle(c.channelId, ev.target.checked)} />
                <label htmlFor={`yt-${i}`}><bdi>{c.title}</bdi>{c.customUrl ? <> (<bdi>{c.customUrl}</bdi>)</> : null}</label>
              </div>
            ))}
          </fieldset>
          <Text tone="muted">{t('social.youtube.readOnly')}</Text>
          <Button type="submit" variant="primary" disabled={!chosen.size}>{t('social.youtube.pick.save')}</Button>
        </form>
      ) : null}
    </section>
  );
}
